import { TEAM_ENDPOINTS, TEAM_FETCH_PAGE_SIZE, TEAM_MAX_PAGES } from "../constants/teamEndpoints";

// The endpoints can answer HTTP 200 with statusType FAILURE/ERROR for business errors (for example a duplicate member)
const unwrap = (data) => {
  if (data?.statusType && data.statusType !== "SUCCESS") {
    throw new Error(data.text || "Request failed");
  }
  return data;
};

// Repeats a paged request until the last page
const fetchAllPages = async (request, listKey) => {
  const rows = [];
  for (let page = 1; page <= TEAM_MAX_PAGES; page += 1) {
    const data = unwrap((await request(page)).data);
    const batch = data[listKey] || [];
    rows.push(...batch);
    if (batch.length < TEAM_FETCH_PAGE_SIZE || (data.totalPage && page >= data.totalPage)) break;
  }
  return rows;
};

export const createPartnerTeamService = (httpClient) => ({
  // Mobile sends { page, size, search }, but the backend only reads page_number / page_size, so mobile never
  // gets past its first 10 teams. Both spellings are sent so the web list is complete.
  listTeams: () =>
    fetchAllPages(
      (page) =>
        httpClient.post(TEAM_ENDPOINTS.list, {
          page,
          size: TEAM_FETCH_PAGE_SIZE,
          search: "",
          page_number: page,
          page_size: TEAM_FETCH_PAGE_SIZE,
        }),
      "partnerTeams"
    ),

  createTeam: async ({ name, description, total_seats }) => {
    const seats = Number(total_seats);
    const response = await httpClient.post(TEAM_ENDPOINTS.save, {
      name,
      description,
      total_seats: seats,
      used_seats: 0,
      available_seats: seats,
      status: "ACTIVE",
    });
    return unwrap(response.data);
  },

  // Mobile sends the whole team back with the three edited fields replaced
  updateTeam: async (team, { name, description, total_seats }) => {
    const response = await httpClient.put(TEAM_ENDPOINTS.save, {
      ...team,
      name,
      description,
      total_seats: Number(total_seats),
    });
    return unwrap(response.data);
  },

  removeTeam: async (id) => unwrap((await httpClient.delete(TEAM_ENDPOINTS.remove(id))).data),

  listMembers: (teamId) =>
    fetchAllPages(
      (page) => httpClient.post(TEAM_ENDPOINTS.members(teamId), { page_number: page, page_size: TEAM_FETCH_PAGE_SIZE, order_by: "DESC" }),
      "partnerTeamMembers"
    ),

  addMember: async (teamId, { first_name, last_name, phone, email, role }) => {
    const response = await httpClient.post(TEAM_ENDPOINTS.addMember, {
      team_id: Number(teamId),
      first_name,
      last_name,
      email,
      phone,
      role,
      status: "ACTIVE",
    });
    const data = unwrap(response.data);
    // A member with this mobile already in the team comes back as SUCCESS with "... already available!"
    if (/already available/i.test(data?.text || "")) {
      throw new Error("A member with this mobile number is already in this team.");
    }
    return data;
  },

  removeMember: async (id) => unwrap((await httpClient.delete(TEAM_ENDPOINTS.removeMember(id))).data),

  // Seats of the partner's plan (0 when there is no subscription), as the mobile Members screen reads it
  fetchSeats: async () => {
    try {
      const response = await httpClient.get(TEAM_ENDPOINTS.subscription);
      return Number(response.data?.ahdUserSubscription?.seats) || 0;
    } catch {
      return 0;
    }
  },
});
